import{ useEffect } from 'react';
import { Button, Card, FormControl, FormHelperText, Grid, IconButton, InputLabel, MenuItem, Select, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import Loader from '../common/Loader';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { addNewUser, changeRole, enableUser, getUsers, handleToggleAddNewUserDialog, handleToggleChangeRoleDialog, handleToggleEnableUserDialog } from '../../store/prediction-slice/usermanagement-slice';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { USEPRoles } from '../layout/menu-items';
import { AppRoutes } from '../../axios/endPoints';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import React from 'react';
import { Header } from '../common/Header';
import { enqueueSnackbar } from '../../store/app-slice/app';
import { handleNotificationVariant } from '../notifications/_common';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const changeRoleSchema = (t : Function) => yup.object().shape({
  role: yup.string().required(t('translations:USER_MANAGEMENT_ROLE_VALIDATION'))
});

const ChangeRoleDialog = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { changeRoleDialog, changeRoleBodyParams } = useAppSelector((state: RootState) => state.usermanagement);
  const { control, handleSubmit, formState: { errors } } = useForm({  
    mode: "onBlur", 
    resolver: yupResolver(changeRoleSchema(t)) 
});

  function handleClose() {
    dispatch(handleToggleChangeRoleDialog({ open: false, body: null }));
  }

  function handleChangeRole(data: any) 
  {
    dispatch(changeRole({...changeRoleBodyParams, newRole: data.role})).then((res : any) => {
        if(res && res.payload) { 
          dispatch(enqueueSnackbar({ message: t(`translations:${res.payload.message}`), options: { variant: handleNotificationVariant(res.payload.success) } }))
        }
      }
    );
  }

  return(
      <Dialog TransitionComponent={Transition} open={changeRoleDialog} onClose={handleClose}>
        <form onSubmit={handleSubmit(handleChangeRole)}>
        <DialogTitle>{t('translations:USER_MANAGEMENT_CHANGE_ROLE')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('translations:USER_MANAGEMENT_CHANGE_ROLE_USER', { email: changeRoleBodyParams?.email })}
          </DialogContentText>
          <br/>
          <Controller control={control} name="role" render={({ field,  fieldState: { error }}) => (
            <FormControl fullWidth error={error !== undefined}>
                <InputLabel id="role-label">{t('translations:USER_MANAGEMENT_ROLE_LABEL')}</InputLabel>
                <Select
                    labelId="role-label"
                    label={t('translations:USER_MANAGEMENT_ROLE_LABEL')}
                    fullWidth
                    variant="outlined"
                    {...field} 
                >
                  {Object.values(USEPRoles)?.map((value) => {
                    return(<MenuItem value={value}>{value}</MenuItem>)
                  })}
                </Select>
                { errors ? <FormHelperText>{errors.role?.message}</FormHelperText> : ''}
            </FormControl>
          )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('translations:CANCEL_BUTTON')}</Button>
          <Button type="submit">{t('translations:SUBMIT_BUTTON')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
const EnableUserDialog = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { enableUserDialog, enableUserBodyParams } = useAppSelector((state: RootState) => state.usermanagement);

  function handleClose() {
    dispatch(handleToggleEnableUserDialog({ open: false, body: null }));
  }

  function handleSubmit() {
    dispatch(enableUser(enableUserBodyParams)).then((res : any) => {
        if(res && res.payload) { 
          dispatch(enqueueSnackbar({ message: t(`translations:${res.payload.message}`), options: { variant: handleNotificationVariant(res.payload.success) } }))
        }
      }
    );
  }

  return(
    <Dialog
      open={enableUserDialog}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>{t('translations:USER_MANAGEMENT_ENABLE_DISABLE_TITLE', { 
        enable: enableUserBodyParams?.enable ? t('translations:USER_MANAGEMENT_ENABLE') : t('translations:USER_MANAGEMENT_DISABLE')
      })}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-slide-description">
          {t('translations:USER_MANAGEMENT_ENABLE_DISABLE_BODY', {
            email: enableUserBodyParams?.email,
            enable: enableUserBodyParams?.enable ? t('translations:USER_MANAGEMENT_ENABLE')?.toLowerCase() : t('translations:USER_MANAGEMENT_DISABLE')?.toLowerCase()
          })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('translations:CANCEL_BUTTON')}</Button>
        <Button onClick={handleSubmit}>{t('translations:SUBMIT_BUTTON')}</Button>
      </DialogActions>
    </Dialog>
  )
}

const schema = (t : Function) => yup.object().shape({
  email: yup.string().email(t('translations:USER_MANAGEMENT_EMAIL_VALIDATION')).required(t('translations:USER_MANAGEMENT_EMAIL_VALIDATION')),
  role: yup.string().required(t('translations:USER_MANAGEMENT_ROLE_VALIDATION'))
});

const AddNewUserDialog = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { addNewUserDialog } = useAppSelector((state: RootState) => state.usermanagement);
  const { control, handleSubmit, formState: { errors } } = useForm({  
    mode: "onBlur", 
    resolver: yupResolver(schema(t)) 
});

  function handleClose() {
    dispatch(handleToggleAddNewUserDialog(false));
  }

  function handleAddNewUser(data: any) 
  {
    const changePasswordUrl = new URL(
      AppRoutes.RESET_PASSWORD,
      window.location.protocol + '//' + window.location.host
    ).href;

    var payload = {
      email: data.email,
      role: data.role,
      changePasswordUrl: changePasswordUrl
    }
    dispatch(addNewUser(payload)).then((res : any) => {
        if(res && res.payload) { 
          dispatch(enqueueSnackbar({ message: t(`translations:${res.payload.message}`), options: { variant: handleNotificationVariant(res.payload.success) } }))
        }
      }
    );
  }

  return(
      <Dialog TransitionComponent={Transition} open={addNewUserDialog} onClose={handleClose}>
        <form onSubmit={handleSubmit(handleAddNewUser)}>
        <DialogTitle>{t('translations:USER_MANAGEMENT_ADD_NEW_USER')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('translations:USER_MANAGEMENT_ADD_USER_INFO_TEXT')}
          </DialogContentText>
          <br/>
          <Controller control={control} name="email" render={({field, fieldState: { error }}) => 
            ( 
                <TextField 
                    {...field} 
                    fullWidth 
                    variant="outlined" 
                    label={t('translations:USER_MANAGEMENT_EMAIL_LABEL')} 
                    type="text"
                    error={error !== undefined}
                    helperText={error ? errors.email?.message : ''}
                />
            )}
          />
          <Controller control={control} name="role" render={({ field,  fieldState: { error }}) => (
            <FormControl style={{ marginTop: '20px' }} fullWidth error={error !== undefined}>
                <InputLabel id="role-label">{t('translations:USER_MANAGEMENT_ROLE_LABEL')}</InputLabel>
                <Select
                    labelId="role-label"
                    label={t('translations:USER_MANAGEMENT_ROLE_LABEL')}
                    fullWidth
                    variant="outlined"
                    {...field} 
                >
                  {Object.values(USEPRoles)?.map((value) => {
                    return(<MenuItem value={value}>{value}</MenuItem>)
                  })}
                </Select>
                { errors ? <FormHelperText>{errors.role?.message}</FormHelperText> : ''}
            </FormControl>
          )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('translations:CANCEL_BUTTON')}</Button>
          <Button type="submit">{t('translations:SUBMIT_BUTTON')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export interface User {
  userID: number,
  username: string,
  email: string,
  role: string,
  isEnabled: boolean,
  isConfirmed: boolean,
  isCurrentUser: boolean
}

const columns = (dispatch: any, t : Function) : GridColDef[] => [
  { field: 'userID', headerName: t('translations:USERMANAGEMENT_ID_COLUMN'), width: 100 },
  { field: 'username', headerName: t('translations:USERMANAGEMENT_USERNAME_COLUMN'), width: 350 },
  { field: 'email', headerName: t('translations:USERMANAGEMENT_EMAIL_COLUMN'), width: 350 },
  { field: 'role', headerName: t('translations:USERMANAGEMENT_ROLE_COLUMN'), width: 250 },
  { field: 'isEnabled', headerName: t('translations:USERMANAGEMENT_ENABLED_COLUMN'), width: 200, renderCell: ({value}) => { return value ? t('translations:YES_LABEL') : t('translations:NO_LABEL') }},
  { field: 'isConfirmed', headerName: t('translations:USERMANAGEMENT_CONFIRMED_COLUMN'), width: 200, renderCell: ({value}) => { return value ? t('translations:YES_LABEL') : t('translations:NO_LABEL') } },
  { field: '', headerName: t('translations:USERMANAGEMENT_ACTION_COLUMN'), width: 200, renderCell: (params) => {
    return <Grid container display="flex" justifyContent="center" spacing={4}>
      <Grid item>
        <Tooltip title={ params.row.isEnabled ? t('translations:USER_MANAGEMENT_DISABLE_USER') : t('translations:USER_MANAGEMENT_ENABLE_USER')}>
          <IconButton aria-label="enable" onClick={() => dispatch(handleToggleEnableUserDialog({ open: true, body: {
            email: params.row.email,
            enable: !params.row.isEnabled
          }}))}>
            { params.row.isEnabled ? <ToggleOffIcon color="primary"/> : <ToggleOnIcon color="disabled"/> }
          </IconButton>
        </Tooltip>
      </Grid>
      <Grid item>
        <Tooltip title={t('translations:USER_MANAGEMENT_CHANGE_ROLE')}>
          <IconButton aria-label="change" onClick={() => {
            dispatch(handleToggleChangeRoleDialog({ open: true, body: {
              email: params.row.email
            }}))
          }}><ManageAccountsIcon color="primary"/></IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  } }
];

export function UserManagement() { 
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { loadingUsers, users, reloadUsers, openLoader } = useAppSelector((state: RootState) => state.usermanagement);

  function handleAddNewUserDialogToggle() 
  {
    dispatch(handleToggleAddNewUserDialog(true));
  }

  useEffect(() => {
    dispatch(getUsers());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadUsers]);

  if(loadingUsers) return <Loader open={loadingUsers} />;

  if (users.length === 0)
    return (
      <Card style={{marginTop: '5px'}}>
        <Header title={t('translations:USER_MANAGEMENT_NO_USERS')}/>
        <Button
          onClick={handleAddNewUserDialogToggle}
          variant="contained"
          startIcon={<AddIcon />}
        >
          {t('translations:USER_MANAGEMENT_ADD_NEW_USER')}
        </Button>
      </Card>
    );

  return (
    <Card style={{marginTop: '5px'}}>
      { openLoader ? <Loader open={openLoader} /> : ''}
      <Header title={t('translations:USER_MANAGEMENT_USERS_LABEL')} />
      <Grid p={1.5} display="flex" justifyContent="flex-end" alignItems="flex-end">
        <Button onClick={() => {
          handleAddNewUserDialogToggle();
        }} className='button-color' variant="contained" startIcon={<AddIcon />}>{t('translations:USER_MANAGEMENT_ADD_NEW_USER')}</Button>
      </Grid>
      <Box sx={{ overflow: "auto" }} p={1.5}>
        <Box sx={{ width: "100%", display: "table", tableLayout: "fixed" }}>
          <DataGrid
            autoHeight
            rows={users}
            columns={columns(dispatch, t)}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 50, 100]}
            getRowId={(row) => row.userID}
            disableRowSelectionOnClick
            disableColumnSelector
            disableDensitySelector
            componentsProps={{
              pagination: {
                labelRowsPerPage: t('translations:ROWS_PER_PAGE_TEXT')
              }
            }}
          />
        </Box>
      </Box>
      <ChangeRoleDialog />
      <EnableUserDialog />
      <AddNewUserDialog />
    </Card>
  );
}

export default UserManagement;
